/**
 * Адреса каталога сервисов. Вкладки «Внутренние / Внешние» — разные адреса списка, как в
 * прежнем портале; вкладки карточки — сегмент пути с прежними значениями портала (info,
 * endpoints, recipients, data-flows): действующие ссылки, крошки и возврат после создания
 * открывают ту же вкладку (спецификация редизайна, раздел 4: старые адреса сохраняются).
 * Поток с другим сервисом — вложенный адрес вкладки связей data-flows/<сервис>/<направление>.
 */
import { type EntityId } from '@/lib/entityId';
import type { AssetFlowDirection } from '@/api/assets';
import { mapNodeKey } from '@/pages/DataMap/mapAdapters';

export const SERVICES_PATH = '/service-catalog/services';
export const EXTERNAL_PATH = '/service-catalog/external-connections';
export const NEW_CONNECTION_PATH = '/service-catalog/external-connections/new';

/** Список вкладки каталога: внутренние или внешние сервисы */
export function catalogPath(external: boolean): string {
	return external ? EXTERNAL_PATH : SERVICES_PATH;
}

/** Вкладки карточки сервиса: значение сегмента пути */
export const SERVICE_TABS = {
	overview: 'info',
	endpoints: 'endpoints',
	recipients: 'recipients',
	flows: 'data-flows',
} as const;

export type ServiceTab = (typeof SERVICE_TABS)[keyof typeof SERVICE_TABS];

/**
 * Вкладка карточки по сегменту адреса. Прежние «connections» портала и «dataflows» первой
 * версии редизайна ведут на связи; неизвестный сегмент — на обзор
 */
export function serviceTabOf(segment: string | undefined): ServiceTab {
	if (segment === 'connections' || segment === 'dataflows') return SERVICE_TABS.flows;
	const known = Object.values(SERVICE_TABS) as string[];
	return segment && known.includes(segment) ? (segment as ServiceTab) : SERVICE_TABS.overview;
}

export function servicePath(
	id: EntityId | string,
	tab: ServiceTab = SERVICE_TABS.overview,
): string {
	const base = `${SERVICES_PATH}/${encodeURIComponent(String(id))}`;
	return tab === SERVICE_TABS.overview ? base : `${base}/${tab}`;
}

/** Поток сервиса с другим сервисом; без известного направления — без сегмента направления */
export function flowDetailPath(
	serviceId: EntityId,
	interactsWith: EntityId,
	direction: AssetFlowDirection,
): string {
	const base = `${servicePath(serviceId, SERVICE_TABS.flows)}/${interactsWith}`;
	return direction ? `${base}/${direction}` : base;
}

/** Узел на карте данных: тип прежней карты и id (DataMap читает параметр node) */
export function mapNodeHref(type: string, id: EntityId | string): string {
	return `/data-map?${new URLSearchParams({ node: mapNodeKey(type, String(id)) })}`;
}

/** Тип узла сервиса на карте данных */
export const SERVICE_MAP_TYPE = 'resource';
